// Seed catalog for the UGC Marketplace MVP storefront (/marketplace).
//
// These are SAMPLE clips for the preview catalog — not real inventory and not
// customer claims. Product names come from the approved mockup list in
// CLAUDE.md (never invent products); creator handles are generic studio-style
// placeholders, surfaced under a "Preview catalog" label so nothing reads as a
// real attribution. Prices are our own launch license prices, not third-party
// numbers.
//
// Thumbnails resolve deterministically through unsplashPlaceholderFor(slug,
// { category }) so the same clip always renders the same lifestyle photo.

import type { PlaceholderCategory } from './placeholder-images'

export type ClipReaction =
  | 'Unboxing'
  | 'Try-on'
  | 'Before / after'
  | 'Review'
  | 'Demo'
  | 'Hype'

export type ClipVertical = 'Fashion' | 'Beauty' | 'Sport' | 'Accessories' | 'Lifestyle'

export type MarketplaceClip = {
  id: string
  /** Short, human title for the clip. */
  title: string
  /** Approved sample product the clip features (CLAUDE.md mockup list). */
  product: string
  /** Launch license price in USD (our price, not a third-party figure). */
  price: number
  reaction: ClipReaction
  vertical: ClipVertical
  /** Image category for unsplashPlaceholderFor. */
  category: PlaceholderCategory
  /** Clip length in seconds. */
  durationSec: number
  /** Generic studio-style placeholder handle (not a real person). */
  creator: string
}

export const VERTICALS: ClipVertical[] = ['Fashion', 'Beauty', 'Sport', 'Accessories', 'Lifestyle']
export const REACTIONS: ClipReaction[] = ['Unboxing', 'Try-on', 'Before / after', 'Review', 'Demo', 'Hype']

export const marketplaceClips: MarketplaceClip[] = [
  { id: 'wrogn-unbox',   title: 'Unboxing the silver-toned watch',        product: 'WROGN Men Silver-Toned Watch',       price: 9,  reaction: 'Unboxing',       vertical: 'Accessories', category: 'fashion',     durationSec: 18, creator: '@studio.reel' },
  { id: 'wrogn-review',  title: 'A week on the wrist, honest take',       product: 'WROGN Men Silver-Toned Watch',       price: 5,  reaction: 'Review',         vertical: 'Accessories', category: 'editorial',   durationSec: 24, creator: '@candid.cuts' },
  { id: 'airlift-shorts-tryon', title: 'High-waist shorts try-on',        product: 'Airlift High-Waist Suit Up Shorts',  price: 12, reaction: 'Try-on',         vertical: 'Sport',       category: 'sport',       durationSec: 15, creator: '@move.lab' },
  { id: 'airlift-shorts-demo',  title: 'Squat-test the high-waist fit',   product: 'Airlift High-Waist Suit Up Shorts',  price: 9,  reaction: 'Demo',           vertical: 'Sport',       category: 'sport',       durationSec: 17, creator: '@move.lab' },
  { id: 'overcoat-tryon', title: 'Brown overcoat, three ways',           product: 'Airlift Overcoat Brown',             price: 12, reaction: 'Try-on',         vertical: 'Fashion',     category: 'fashion',     durationSec: 21, creator: '@layer.studio' },
  { id: 'sweater-hype',   title: 'Green-stripe sweater first reaction',  product: 'White Sweater Green Stripes',        price: 9,  reaction: 'Hype',           vertical: 'Fashion',     category: 'fashion',     durationSec: 12, creator: '@frame.house' },
  { id: 'sweater-review', title: 'Does it pill? 30-day review',          product: 'White Sweater Green Stripes',        price: 9,  reaction: 'Review',         vertical: 'Fashion',     category: 'editorial',   durationSec: 19, creator: '@frame.house' },
  { id: 'spf-beforeafter',title: 'SPF 50 before / after, no filter',     product: 'Bodycon Sunscreen SPF 50',           price: 19, reaction: 'Before / after', vertical: 'Beauty',      category: 'beauty',      durationSec: 30, creator: '@glow.notes' },
  { id: 'spf-demo',       title: 'How it sits under makeup',             product: 'Bodycon Sunscreen SPF 50',           price: 9,  reaction: 'Demo',           vertical: 'Beauty',      category: 'beauty',      durationSec: 16, creator: '@glow.notes' },
  { id: 'sunnies-tryon',  title: 'Oval sunglasses on five faces',        product: 'Women Oval Sunglasses',              price: 5,  reaction: 'Try-on',         vertical: 'Accessories', category: 'fashion',     durationSec: 11, creator: '@sunday.films' },
  { id: 'sunnies-hype',   title: 'Golden-hour sunglasses moment',        product: 'Women Oval Sunglasses',              price: 5,  reaction: 'Hype',           vertical: 'Accessories', category: 'editorial',   durationSec: 8,  creator: '@sunday.films' },
  { id: 'cardigan-review',title: 'Tennis cardigan for spring layering',  product: 'Tennis Cardigan Spring/Summer',      price: 9,  reaction: 'Review',         vertical: 'Sport',       category: 'sport',       durationSec: 22, creator: '@court.side' },
  { id: 'tote-unbox',     title: 'Jute tote, what fits inside',          product: 'Jute Tote Bag',                      price: 5,  reaction: 'Unboxing',       vertical: 'Lifestyle',   category: 'commerce',    durationSec: 14, creator: '@slow.haul' },
  { id: 'tote-hype',      title: 'The everyday tote, in one shot',       product: 'Jute Tote Bag',                      price: 5,  reaction: 'Hype',           vertical: 'Lifestyle',   category: 'commerce',    durationSec: 9,  creator: '@slow.haul' },
]

// Accessors — keep the rest of the app off the raw array so the seed can be
// swapped for a Sanity/DB source later without touching consumers.

export const clipHref = (id: string): string => `/marketplace/clip/${id}`

export function getClipById(id: string): MarketplaceClip | undefined {
  return marketplaceClips.find((c) => c.id === id)
}

export function relatedClips(id: string, limit = 4): MarketplaceClip[] {
  const clip = getClipById(id)
  if (!clip) return marketplaceClips.slice(0, limit)
  // Prefer same vertical, then same reaction, never the clip itself.
  const scored = marketplaceClips
    .filter((c) => c.id !== id)
    .map((c) => ({
      c,
      score: (c.vertical === clip.vertical ? 2 : 0) + (c.reaction === clip.reaction ? 1 : 0),
    }))
    .sort((a, b) => b.score - a.score)
  return scored.slice(0, limit).map((s) => s.c)
}
